import React, { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Icon from "../../components/AppIcon"
import { registerOrganization, claimOrganizationNesolearnId } from "../../utils/orgHelpers"
import { normalizeUsername, isValidUsername } from "../../utils/nesolearnIdHelpers"

const ORG_TYPE_OPTIONS = [
  { value: "University", label: "University" },
  { value: "Training Institute", label: "Training Institute" },
  { value: "Professional Organization", label: "Professional Organization" },
  { value: "Blockchain Community", label: "Blockchain Community" },
  { value: "Technology Company", label: "Technology Company" },
  { value: "Individual Educator", label: "Individual Educator" },
]

export const InstitutionFormPanel = () => {
  const navigate = useNavigate()

  // ---- form state / logic (unchanged) ----
  const [formData, setFormData] = useState({
    ownerName: "",
    email: "",
    password: "",
    orgName: "",
    orgType: "",
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")

  const [showClaimModal, setShowClaimModal] = useState(false)
  const [pendingOrg, setPendingOrg] = useState(null)
  const [claimUsername, setClaimUsername] = useState("")
  const [claimError, setClaimError] = useState("")
  const [isClaiming, setIsClaiming] = useState(false)
  const [claimedId, setClaimedId] = useState("")
  const [copied, setCopied] = useState(false)

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const validate = () => {
    const newErrors = {}
    if (!formData?.ownerName?.trim()) newErrors.ownerName = "Your name is required"
    if (!formData?.email?.trim()) newErrors.email = "Email is required"
    if (!formData?.password || formData?.password?.length < 6) newErrors.password = "Password must be at least 6 characters"
    if (!formData?.orgName?.trim()) newErrors.orgName = "Organization name is required"
    if (!formData?.orgType) newErrors.orgType = "Please select an organization type"
    setErrors(newErrors)
    return Object.keys(newErrors)?.length === 0
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    setSubmitError("")
    if (!validate()) return
    setIsSubmitting(true)

    const { user, orgId, error } = await registerOrganization({
      ownerName: formData?.ownerName,
      email: formData?.email,
      password: formData?.password,
      orgName: formData?.orgName,
      orgType: formData?.orgType,
    })

    setIsSubmitting(false)

    if (error) {
      setSubmitError(error?.message || "Something went wrong. Please try again.")
      return
    }

    setPendingOrg({ orgId, uid: user?.uid, email: formData?.email })
    setClaimUsername("")
    setClaimError("")
    setShowClaimModal(true)
  }

  const handleClaimSubmit = async (e) => {
    e?.preventDefault()
    setClaimError("")

    const clean = normalizeUsername(claimUsername)
    if (!isValidUsername(clean)) {
      setClaimError("Use 3-20 characters, starting with a letter — letters, numbers, _ or - only.")
      return
    }

    setIsClaiming(true)
    const { nesolearnId, error } = await claimOrganizationNesolearnId(
      pendingOrg?.orgId,
      pendingOrg?.uid,
      pendingOrg?.email,
      clean
    )
    setIsClaiming(false)

    if (error) {
      setClaimError(error?.message || "Couldn't claim that ID. Try another.")
      return
    }

    setClaimedId(nesolearnId)
  }

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(claimedId)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      // Clipboard API can fail on non-HTTPS/older browsers — ID is still visible on screen
    }
  }

  const finishAndGoToOnboarding = () => {
    setShowClaimModal(false)
    navigate("/organization-onboarding", { replace: true })
  }

  // ---- Mercury visual layer (from MercuryLogin) ----
  const blobsData = useMemo(() => {
    return Array.from({ length: 4 }).map(() => ({
      size: Math.random() * 200 + 150,
      left: Math.random() * 80 + 10,
      top: Math.random() * 80 + 10,
      animationDelay: Math.random() * -20,
      animationDuration: Math.random() * 15 + 15,
    }))
  }, [])

  const blobRefs = useRef([])

  useEffect(() => {
    // Skip parallax on touch devices — no real pointer, and it's wasted cycles on mobile
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) {
      return
    }

    let frame = null
    let latestEvent = null

    const applyParallax = () => {
      if (latestEvent) {
        const x = latestEvent.clientX / window.innerWidth
        const y = latestEvent.clientY / window.innerHeight
        blobRefs.current.forEach((blob, index) => {
          if (blob) {
            const speed = (index + 1) * 10
            blob.style.marginLeft = `${x * speed}px`
            blob.style.marginTop = `${y * speed}px`
          }
        })
      }
      frame = null
    }

    const handleMouseMove = (e) => {
      latestEvent = e
      if (frame === null) {
        frame = requestAnimationFrame(applyParallax)
      }
    }

    document.addEventListener("mousemove", handleMouseMove)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div className="mercury-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;800&family=Space+Mono&display=swap');

        .mercury-wrapper {
          --bg: #050505;
          --mercury: #e0e0e0;
          --accent: #ffffff;
          --text-dim: rgba(255, 255, 255, 0.5);
          --filter-goo: url('#institution-gooey');
          background-color: var(--bg);
          color: var(--accent);
          font-family: 'Inter', sans-serif;
          min-height: 100vh;
          width: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 48px 0;
          position: relative;
        }

        .mercury-wrapper * {
          box-sizing: border-box;
          -webkit-font-smoothing: antialiased;
        }

        .mercury-wrapper .stage {
          position: absolute;
          width: 100%;
          height: 100%;
          z-index: 0;
          filter: var(--filter-goo);
          opacity: 0.6;
        }

        .mercury-wrapper .blob {
          position: absolute;
          background: linear-gradient(135deg, var(--mercury), #888);
          border-radius: 50%;
          filter: blur(14px);
          animation: institution-float 24s infinite alternate ease-in-out;
          will-change: transform, margin;
          box-shadow: inset -10px -10px 20px rgba(0,0,0,0.5),
                      10px 10px 30px rgba(255,255,255,0.2);
          transition: margin 0.15s ease-out;
        }
        @keyframes institution-float {
          0% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(10vw, 20vh) scale(1.2); }
          66% { transform: translate(-5vw, 10vh) scale(0.8); }
          100% { transform: translate(5vw, -10vh) scale(1.1); }
        }

        .mercury-wrapper .auth-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 440px;
          padding: 40px;
        }

        .mercury-wrapper .header {
          margin-bottom: 40px;
          text-align: left;
        }

        .mercury-wrapper .brand-id {
          font-family: 'Space Mono', monospace;
          font-size: 10px;
          letter-spacing: 4px;
          text-transform: uppercase;
          color: var(--text-dim);
          margin-bottom: 8px;
          display: block;
        }

        .mercury-wrapper .header h1 {
          font-weight: 800;
          font-size: 2.75rem;
          line-height: 0.95;
          letter-spacing: -2px;
          margin-left: -2px;
          margin-top: 0;
          margin-bottom: 12px;
        }

        .mercury-wrapper .header p {
          color: var(--text-dim);
          font-size: 14px;
          line-height: 1.5;
          max-width: 380px;
        }

        .mercury-wrapper .header p a,
        .mercury-wrapper .header p button {
          color: var(--accent);
          text-decoration: underline;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          font: inherit;
        }

        .mercury-wrapper .form-group {
          position: relative;
          margin-bottom: 26px;
          transition: transform 0.4s cubic-bezier(0.2, 1, 0.3, 1);
        }

        .mercury-wrapper .form-group:focus-within {
          transform: translateX(10px);
        }

        .mercury-wrapper .form-group label {
          display: block;
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: var(--text-dim);
          margin-bottom: 12px;
          text-transform: uppercase;
        }

        .mercury-wrapper .form-group input,
        .mercury-wrapper .form-group select {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          color: var(--accent);
          padding: 12px 0;
          font-size: 18px;
          outline: none;
          transition: border-color 0.4s;
          font-family: 'Inter', sans-serif;
        }

        .mercury-wrapper .form-group select option {
          background: #0a0a0a;
          color: var(--accent);
        }

        .mercury-wrapper .input-glow {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0%;
          height: 2px;
          background: var(--mercury);
          transition: width 0.6s cubic-bezier(0.2, 1, 0.3, 1);
          box-shadow: 0 0 15px var(--mercury);
        }

        .mercury-wrapper .form-group input:focus + .input-glow,
        .mercury-wrapper .form-group select:focus + .input-glow {
          width: 100%;
        }

        .mercury-wrapper .field-error {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          color: #ff8080;
          margin-top: 8px;
        }

        .mercury-wrapper .submit-error {
          font-family: 'Space Mono', monospace;
          font-size: 12px;
          color: #ff8080;
          margin-top: -10px;
          margin-bottom: 20px;
        }

        .mercury-wrapper .submit-wrap {
          margin-top: 40px;
          position: relative;
        }

        .mercury-wrapper .btn-base {
          background: var(--accent);
          color: #000;
          border: none;
          padding: 20px 40px;
          font-size: 14px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 2px;
          cursor: pointer;
          width: 100%;
          position: relative;
          z-index: 2;
          transition: letter-spacing 0.3s, opacity 0.3s;
        }

        .mercury-wrapper .btn-base:hover {
          letter-spacing: 4px;
        }

        .mercury-wrapper .btn-base:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mercury-wrapper .mercury-drop {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background: var(--mercury);
          transform: translate(-50%, -50%);
          z-index: 1;
          border-radius: 50px;
          transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .mercury-wrapper .submit-wrap:hover .mercury-drop {
          transform: translate(-50%, -50%) scale(1.05, 1.2);
          filter: brightness(1.2);
        }

        .mercury-wrapper .footer-nav {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
          font-family: 'Space Mono', monospace;
          font-size: 10px;
        }

        .mercury-wrapper .footer-nav button {
          background: none;
          border: none;
          color: var(--text-dim);
          transition: color 0.3s;
          cursor: pointer;
          font-family: 'Space Mono', monospace;
          font-size: 10px;
          padding: 0;
        }

        .mercury-wrapper .footer-nav button:hover {
          color: var(--accent);
        }

        .mercury-wrapper .svg-filter-hidden {
          position: absolute;
          width: 0;
          height: 0;
        }

        /* Claim ID modal — restyled to match the dark theme */
        .mercury-wrapper .claim-overlay {
          position: fixed;
          inset: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.7);
          backdrop-filter: blur(4px);
          padding: 16px;
        }

        .mercury-wrapper .claim-card {
          background: #0d0d0d;
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 32px;
          max-width: 400px;
          width: 100%;
          text-align: center;
        }

        .mercury-wrapper .claim-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(255,255,255,0.08);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
        }

        .mercury-wrapper .claim-title {
          font-size: 18px;
          font-weight: 700;
          margin-bottom: 6px;
        }

        .mercury-wrapper .claim-copy {
          font-size: 13px;
          color: var(--text-dim);
          margin-bottom: 20px;
          line-height: 1.5;
        }

        .mercury-wrapper .claim-input-row {
          display: flex;
          align-items: center;
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 8px;
          padding: 0 12px;
          height: 48px;
          margin-bottom: 8px;
          background: rgba(255,255,255,0.03);
        }

        .mercury-wrapper .claim-input-row input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: var(--accent);
          text-align: right;
          font-weight: 500;
        }

        .mercury-wrapper .claim-suffix {
          color: var(--text-dim);
          font-weight: 500;
          margin-left: 4px;
        }

        .mercury-wrapper .claim-error {
          font-size: 12px;
          color: #ff8080;
          margin-bottom: 12px;
        }

        .mercury-wrapper .claim-btn {
          background: var(--accent);
          color: #000;
          border: none;
          padding: 12px 28px;
          font-weight: 700;
          font-size: 13px;
          border-radius: 8px;
          cursor: pointer;
          width: auto;
        }

        .mercury-wrapper .claim-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .mercury-wrapper .claim-skip {
          display: block;
          width: 100%;
          text-align: center;
          font-size: 13px;
          color: var(--text-dim);
          text-decoration: underline;
          margin-top: 16px;
          background: none;
          border: none;
          cursor: pointer;
        }

        .mercury-wrapper .claimed-id {
          font-size: 20px;
          font-weight: 700;
          color: var(--accent);
          background: rgba(255,255,255,0.05);
          border-radius: 10px;
          padding: 14px;
          margin-bottom: 20px;
          word-break: break-all;
        }
      `}</style>

      <svg className="svg-filter-hidden">
        <defs>
          <filter id="institution-gooey">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" />
          </filter>
        </defs>
      </svg>

      <div className="stage">
        {blobsData.map((data, index) => (
          <div
            key={index}
            ref={(el) => (blobRefs.current[index] = el)}
            className="blob"
            style={{
              width: `${data.size}px`,
              height: `${data.size}px`,
              left: `${data.left}%`,
              top: `${data.top}%`,
              animationDelay: `${data.animationDelay}s`,
              animationDuration: `${data.animationDuration}s`,
            }}
          />
        ))}
      </div>

      <main className="auth-container">
        <header className="header">
          <span className="brand-id">Nesolearn / Institution Access</span>
          <h1>REGISTER<br/>YOUR ORG</h1>
          <p>
            Create your organization account to deliver courses, track learners, and issue verifiable certificates.{" "}
            <button type="button" onClick={() => navigate("/authentication-portal")}>
              Sign in instead
            </button>
          </p>
        </header>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              placeholder="e.g. John Gilbert"
              value={formData?.ownerName}
              onChange={handleChange("ownerName")}
              required
            />
            <div className="input-glow"></div>
            {errors?.ownerName && <div className="field-error">{errors.ownerName}</div>}
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="you@organization.com"
              value={formData?.email}
              onChange={handleChange("email")}
              required
            />
            <div className="input-glow"></div>
            {errors?.email && <div className="field-error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={formData?.password}
              onChange={handleChange("password")}
              required
            />
            <div className="input-glow"></div>
            {errors?.password && <div className="field-error">{errors.password}</div>}
          </div>

          <div className="form-group">
            <label>Organization Name</label>
            <input
              type="text"
              placeholder="e.g. Africa Web3 Institute"
              value={formData?.orgName}
              onChange={handleChange("orgName")}
              required
            />
            <div className="input-glow"></div>
            {errors?.orgName && <div className="field-error">{errors.orgName}</div>}
          </div>

          <div className="form-group">
            <label>Organization Type</label>
            <select value={formData?.orgType} onChange={handleChange("orgType")} required>
              <option value="" disabled>Select organization type</option>
              {ORG_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div className="input-glow"></div>
            {errors?.orgType && <div className="field-error">{errors.orgType}</div>}
          </div>

          {submitError && <div className="submit-error">{submitError}</div>}

          <div className="submit-wrap">
            <div className="mercury-drop"></div>
            <button type="submit" className="btn-base" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Organization Account"}
            </button>
          </div>
        </form>

        <footer className="footer-nav">
          <button type="button" onClick={() => navigate("/authentication-portal")}>SIGN IN</button>
          <button type="button" onClick={() => navigate("/home-landing-page")}>HOME</button>
        </footer>
      </main>

      {showClaimModal && (
        <div className="claim-overlay">
          <div className="claim-card">
            {!claimedId ? (
              <form onSubmit={handleClaimSubmit}>
                <div className="claim-icon">
                  <Icon name="IdCard" size={24} color="#ffffff" />
                </div>
                <div className="claim-title">Claim Your Organization's Nesolearn ID</div>
                <div className="claim-copy">
                  This is your organization's identity across Nesolearn. Members and learners can find and verify you by it — and your team can use it to sign in.
                </div>
                <div className="claim-input-row">
                  <input
                    type="text"
                    placeholder="yourorg"
                    value={claimUsername}
                    onChange={(e) => setClaimUsername(e?.target?.value)}
                    autoFocus
                  />
                  <span className="claim-suffix">.nesol</span>
                </div>
                {claimError && <div className="claim-error">{claimError}</div>}
                <button type="submit" className="claim-btn" disabled={isClaiming}>
                  {isClaiming ? "Claiming..." : "Claim ID"}
                </button>
                <button type="button" onClick={finishAndGoToOnboarding} className="claim-skip">
                  Skip for now
                </button>
              </form>
            ) : (
              <>
                <div className="claim-icon">
                  <Icon name="PartyPopper" size={24} color="#ffffff" />
                </div>
                <div className="claim-title">ID Claimed!</div>
                <div className="claim-copy">Save this — your team can use it to sign in anytime.</div>
                <div className="claimed-id">{claimedId}</div>
                <button type="button" className="claim-btn" onClick={handleCopyId}>
                  {copied ? "Copied!" : "Copy ID"}
                </button>
                <button type="button" onClick={finishAndGoToOnboarding} className="claim-skip">
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default InstitutionFormPanel